/* BP-013 — Subject-To Intelligence tab.
   Existing loan stays in place; investor takes title. Cash-to-close + risk warnings. */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, RotateCcw, Save, Download, KeyRound, ShieldCheck } from "lucide-react";

const INP = "w-full bg-white border border-slate-200 rounded-md px-2 py-1.5 text-sm text-slate-900 focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100";

const EMPTY = {
  purchase_price: "",
  existing_loan_balance: "", existing_monthly_pi: "",
  existing_interest_rate: "", remaining_term_months: "",
  arrears_amount: "0", seller_cash: "0",
  closing_costs: "", repair_budget: "0", cash_reserve: "0",
  monthly_rent: "", monthly_escrow: "0", monthly_other: "0",
  arv: "",
  planned_exit: "hold_rental",
  due_on_sale_status: "unknown",
  loan_source: "user_supplied",
};

export default function SubjectToTab({ property }) {
  const [inputs, setInputs] = useState({
    ...EMPTY,
    purchase_price: property.asking_price || "",
    monthly_rent: property.published_rent || "",
    arv: property.arv || property.estimated_arv || "",
  });
  const [scenarioName, setScenarioName] = useState("Base Terms");
  const [current, setCurrent] = useState(null);
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/v3/properties/${property.id}/subject-to`);
      setAnalyses(data.items || []);
      if (data.items?.[0] && !current) {
        setCurrent(data.items[0]);
        setInputs({ ...EMPTY, ...data.items[0].inputs });
        setScenarioName(data.items[0].scenario_name);
      }
    } catch { toast.error("Failed to load Subject-To"); }
    finally { setLoading(false); }
  }, [property.id, current]);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setInputs((s) => ({ ...s, [k]: e.target.value }));

  const calc = useCallback(async (save) => {
    if (!inputs.purchase_price || !inputs.existing_loan_balance || !inputs.existing_monthly_pi) {
      toast.error("Purchase Price, Existing Loan Balance, and Monthly P&I are required.");
      return;
    }
    setCalculating(true);
    try {
      const { data } = await api.post(
        `/v3/properties/${property.id}/subject-to/calculate`,
        { inputs, save, scenario_name: scenarioName });
      if (save) {
        setCurrent(data);
        toast.success(`${data.scenario_name} saved`);
        load();
      } else {
        setCurrent({ inputs, outputs: data.outputs, scenario_name: scenarioName });
        toast.success("Calculated");
      }
    } catch (err) { toast.error(err?.response?.data?.detail || "Calculation failed"); }
    finally { setCalculating(false); }
  }, [inputs, property.id, scenarioName, load]);

  const publish = useCallback(async () => {
    if (!current?.id) { toast.error("Save the scenario first."); return; }
    if (!current.outputs?.eligible_for_publish) {
      toast.error("Cannot publish: existing loan data is incomplete.");
      return;
    }
    setPublishing(true);
    try {
      const { data } = await api.post(`/v3/subject-to/${current.id}/publish`, {});
      setCurrent(data);
      toast.success(`v${data.version} published`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Publish failed"); }
    finally { setPublishing(false); }
  }, [current, load]);

  const exportPdf = useCallback(() => {
    if (!current?.id) return;
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/v3/subject-to/${current.id}/export/pdf`, "_blank");
  }, [current]);

  const outs = useMemo(() => current?.outputs || {}, [current]);
  const score = outs.subject_to_score ? parseInt(String(outs.subject_to_score), 10) : null;
  const band = score == null ? null
    : score >= 85 ? { c: "text-emerald-700", d: "bg-emerald-500", l: "EXCELLENT" }
    : score >= 70 ? { c: "text-lime-700",    d: "bg-lime-500",    l: "STRONG" }
    : score >= 55 ? { c: "text-amber-700",   d: "bg-amber-500",   l: "WORKABLE" }
    :                { c: "text-red-700",    d: "bg-red-500",     l: "HIGH RISK" };

  const isPublished = Boolean(current?.published_at);

  if (loading) return (
    <div className="flex items-center gap-2 text-slate-600 text-sm" data-testid="st-loading">
      <Loader2 size={14} className="animate-spin"/> Loading…
    </div>
  );

  return (
    <div className="space-y-4" data-testid="tab-panel-subject-to">
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-500 mb-3">
          <KeyRound size={12}/> Subject-To Acquisition — Existing Loan Stays
        </div>

        <Section title="Existing Loan">
          <F l="Existing Loan Balance *"><input value={inputs.existing_loan_balance} onChange={set("existing_loan_balance")} data-testid="st-loan-balance" className={INP} type="number"/></F>
          <F l="Existing Monthly P&I *"><input value={inputs.existing_monthly_pi} onChange={set("existing_monthly_pi")} data-testid="st-monthly-pi" className={INP} type="number"/></F>
          <F l="Interest Rate (0.045 = 4.5%)"><input value={inputs.existing_interest_rate} onChange={set("existing_interest_rate")} className={INP} type="number" step="0.001"/></F>
          <F l="Remaining Term (months)"><input value={inputs.remaining_term_months} onChange={set("remaining_term_months")} className={INP} type="number"/></F>
          <F l="Loan Source">
            <select value={inputs.loan_source} onChange={set("loan_source")} className={INP} data-testid="st-loan-source">
              <option value="user_supplied">User Supplied</option>
              <option value="provider_sourced">Provider Sourced</option>
            </select>
          </F>
          <F l="Due-on-Sale Status">
            <select value={inputs.due_on_sale_status} onChange={set("due_on_sale_status")} className={INP} data-testid="st-due-on-sale">
              <option value="unknown">Unknown</option>
              <option value="acknowledged">Acknowledged</option>
              <option value="waived">Waived</option>
            </select>
          </F>
        </Section>

        <Section title="Deal & Cash-to-Close">
          <F l="Purchase Price *"><input value={inputs.purchase_price} onChange={set("purchase_price")} data-testid="st-purchase" className={INP} type="number"/></F>
          <F l="ARV (blank → property ARV)"><input value={inputs.arv} onChange={set("arv")} className={INP} type="number" placeholder="auto"/></F>
          <F l="Reinstatement / Arrears"><input value={inputs.arrears_amount} onChange={set("arrears_amount")} className={INP} type="number"/></F>
          <F l="Cash to Seller"><input value={inputs.seller_cash} onChange={set("seller_cash")} className={INP} type="number"/></F>
          <F l="Closing Costs (blank → 1.5%)"><input value={inputs.closing_costs} onChange={set("closing_costs")} className={INP} type="number" placeholder="auto"/></F>
          <F l="Repair Budget"><input value={inputs.repair_budget} onChange={set("repair_budget")} className={INP} type="number"/></F>
          <F l="Cash Reserve"><input value={inputs.cash_reserve} onChange={set("cash_reserve")} className={INP} type="number"/></F>
          <F l="Planned Exit">
            <select value={inputs.planned_exit} onChange={set("planned_exit")} className={INP} data-testid="st-exit">
              <option value="hold_rental">Hold — Rental</option>
              <option value="sell_retail">Sell — Retail</option>
              <option value="refinance">Refinance</option>
              <option value="wholetail">Wholetail</option>
            </select>
          </F>
        </Section>

        <Section title="Monthly Income & Obligations">
          <F l="Monthly Rent"><input value={inputs.monthly_rent} onChange={set("monthly_rent")} data-testid="st-rent" className={INP} type="number"/></F>
          <F l="Taxes/Insurance Escrow"><input value={inputs.monthly_escrow} onChange={set("monthly_escrow")} className={INP} type="number"/></F>
          <F l="HOA / Other"><input value={inputs.monthly_other} onChange={set("monthly_other")} className={INP} type="number"/></F>
          <F l="Scenario Name"><input value={scenarioName} onChange={(e) => setScenarioName(e.target.value)} className={INP}/></F>
        </Section>

        <div className="mt-3 flex flex-wrap gap-2">
          <button onClick={() => calc(false)} disabled={calculating} data-testid="st-calc"
                  className="inline-flex items-center gap-1 rounded-md bg-slate-900 text-white text-xs font-semibold px-3 py-1.5 hover:bg-slate-700 disabled:opacity-50">
            {calculating ? <Loader2 size={12} className="animate-spin"/> : <RotateCcw size={12}/>} Calculate
          </button>
          <button onClick={() => calc(true)} disabled={calculating || isPublished} data-testid="st-save"
                  className="inline-flex items-center gap-1 rounded-md bg-emerald-600 text-white text-xs font-semibold px-3 py-1.5 hover:bg-emerald-500 disabled:opacity-50">
            <Save size={12}/> Save Version
          </button>
          <button onClick={publish} disabled={publishing || isPublished || !current?.id || !current?.outputs?.eligible_for_publish}
                  data-testid="st-publish"
                  className="inline-flex items-center gap-1 rounded-md bg-amber-600 text-white text-xs font-semibold px-3 py-1.5 hover:bg-amber-500 disabled:opacity-50"
                  title={isPublished ? "Already published" : !current?.outputs?.eligible_for_publish ? "Loan data required" : "Publish this version"}>
            {publishing ? <Loader2 size={12} className="animate-spin"/> : <ShieldCheck size={12}/>} {isPublished ? "Published" : "Publish"}
          </button>
          <button onClick={exportPdf} disabled={!current?.id} data-testid="st-pdf"
                  className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white text-slate-800 text-xs font-semibold px-3 py-1.5 hover:border-slate-400 disabled:opacity-40">
            <Download size={12}/> PDF
          </button>
        </div>
      </div>

      {current && (<>
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-5" data-testid="st-results">
          <div className="flex flex-wrap items-start justify-between gap-5">
            <div className="min-w-[240px]">
              <div className="text-[10px] uppercase tracking-wider text-amber-800/80">Total Cash Needed</div>
              <div className="text-4xl font-bold tabular-nums mt-1 text-slate-900"
                   data-testid="st-cash-required">${fmt(outs.cash_required)}</div>
              <div className="text-xs text-slate-700 mt-1">
                Monthly Obligation <b>${fmt(outs.monthly_obligation)}</b>
                {" · "}CF <b className={parseFloat(outs.monthly_cash_flow) < 0 ? "text-red-700" : ""}>
                  ${fmt(outs.monthly_cash_flow)}
                </b>
              </div>
              <div className="text-[11px] text-slate-600 mt-1">
                Equity: <b>${fmt(outs.initial_equity)}</b> ({pct(outs.equity_pct)})
                {" · "}DSCR <b>{ratio(outs.dscr)}</b>{" · "}CoC <b>{pct(outs.cash_on_cash)}</b>
              </div>
              <div className="text-[11px] text-slate-600 mt-1">
                Investor All-In: <b>${fmt(outs.investor_all_in)}</b>
                {" · "}5yr Paydown: <b>${fmt(outs.five_year_principal_paydown)}</b>
              </div>
            </div>
            <div className="flex flex-col items-center min-w-[140px]">
              <div className="text-[10px] uppercase tracking-wider text-amber-800/80">Subject-To Score</div>
              <div className="flex items-baseline gap-1 mt-1">
                <span className={`text-5xl font-bold tabular-nums ${band?.c || "text-slate-400"}`} data-testid="st-score">{score ?? "—"}</span>
                <span className="text-sm text-slate-500 font-medium">/100</span>
              </div>
              {band && (
                <div className={`mt-1 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider ${band.c}`}>
                  <span className={`w-2 h-2 rounded-full ${band.d}`}/> {band.l}
                </div>
              )}
              {isPublished && (
                <div className="mt-2 text-[9px] uppercase tracking-wider font-semibold text-amber-800 bg-amber-100 rounded px-2 py-0.5">
                  Published
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="grid gap-3 grid-cols-2 sm:grid-cols-4">
          <Metric l="Existing Loan" v={`$${fmt(inputs.existing_loan_balance)}`}/>
          <Metric l="Break-Even Occ" v={pct(outs.break_even_occupancy)}/>
          <Metric l="Annual Cash Flow" v={`$${fmt(outs.annual_cash_flow)}`}/>
          <Metric l="NOI (annual)" v={`$${fmt(outs.noi)}`}/>
          <Metric l="Cash to Seller" v={`$${fmt(inputs.seller_cash)}`}/>
          <Metric l="Reinstatement" v={`$${fmt(inputs.arrears_amount)}`}/>
          <Metric l="Op Reserves" v={`$${fmt(outs.operating_reserves)}`}/>
          <Metric l="Loan Source" v={(inputs.loan_source || "user_supplied").replace("_", " ")}/>
        </div>

        {(outs.warnings || []).length > 0 && (
          <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-1" data-testid="st-warnings">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Risk & Disclosure Warnings</div>
            {outs.warnings.map((w, i) => (
              <div key={i} className="text-[11px] text-slate-800" data-testid={`st-warning-${w.code}`}>
                <span className={`text-[9px] px-1 py-0.5 rounded uppercase font-semibold mr-2 ${
                  w.severity === "critical" ? "bg-red-100 text-red-700"
                  : w.severity === "high" ? "bg-orange-100 text-orange-700"
                  : w.severity === "medium" ? "bg-amber-100 text-amber-700"
                  : "bg-slate-100 text-slate-700"
                }`}>{w.severity}</span>{w.message}
              </div>
            ))}
          </div>
        )}
      </>)}

      {analyses.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Saved Analyses ({analyses.length})</div>
          <div className="space-y-1">
            {analyses.map((a) => (
              <button key={a.id}
                      onClick={() => { setCurrent(a); setInputs({ ...EMPTY, ...a.inputs }); setScenarioName(a.scenario_name || "Base Terms"); }}
                      data-testid={`st-history-${a.id.slice(0, 8)}`}
                      className="w-full text-left flex items-center justify-between text-xs px-2 py-1.5 rounded hover:bg-slate-50">
                <span>
                  v{a.version} · {a.scenario_name}
                  {a.published_at && <span className="ml-2 text-[9px] uppercase font-semibold text-amber-700">published</span>}
                </span>
                <span className="tabular-nums text-slate-600">
                  Score {a.outputs?.subject_to_score || "—"} · CF/mo ${fmt(a.outputs?.monthly_cash_flow)}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const Section = ({ title, children }) => (
  <div className="mb-3">
    <div className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold mb-2">{title}</div>
    <div className="grid gap-3 sm:grid-cols-3">{children}</div>
  </div>
);
const F = ({ l, children }) => (
  <label className="block">
    <div className="text-[9px] uppercase tracking-wider text-slate-500 mb-1">{l}</div>{children}
  </label>
);
const Metric = ({ l, v }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-3">
    <div className="text-[9px] uppercase tracking-wider text-slate-500">{l}</div>
    <div className="text-base font-semibold text-slate-900 tabular-nums mt-0.5">{v}</div>
  </div>
);
function fmt(v){ if(v==null||v==="")return "—"; const n=parseFloat(String(v)); return Number.isNaN(n)?String(v):n.toLocaleString(undefined,{maximumFractionDigits:0}); }
function pct(v){ if(v==null||v==="")return "—"; const n=parseFloat(String(v)); return Number.isNaN(n)?String(v):`${(n*100).toFixed(1)}%`; }
function ratio(v){ if(v==null||v==="")return "—"; const n=parseFloat(String(v)); return Number.isNaN(n)?String(v):n.toFixed(2); }
